import axios, { AxiosInstance, AxiosRequestConfig } from "axios";
import axiosRetry from "axios-retry";
import { ApiConfig } from "./types/apiConfig.type";
import { getPosSessionIdFromCloud } from "./api/getPosSessionIdFromCloud";
import { returnToaster } from "./utils/Toaster";

const SHOP_ID_HEADER = "X-Amz-Shopid";

export default function (config: ApiConfig): AxiosInstance {
    const customAxios = axios.create({
        withCredentials: true,
        baseURL: config.url,
        responseType: "json" as const,
        headers: {
            "Content-Type": "application/json", //any other common headers go here
        },
        timeout: 20 * 1000, // set to 10sec we can change or remove as the request will be aborted after 10 sec
    });

    axiosRetry(customAxios, {
        retries: 3,
        retryDelay: axiosRetry.exponentialDelay,
        retryCondition: async (error: any) => {
            const status = error?.response?.status;
            const errorCode = error?.response?.data?.errorCode || error?.response?.data?.error;

            if (status === 403 && errorCode === "FORBIDDEN") {
                // Call getPosSessionIdFromCloud when a 403 error occurs
                await getPosSessionIdFromCloud();
                return true; // Allow retry
            }
            return false;
        },
    });

    // response handler that gets fired from interceptor
    const responseHandler = (response: any) => {
        if (response.token) {
            // store the token in session/localstorage -> for native need to figure out ways or call callback from the App which does the storing & retrieval of tokens
        }

        // Error handling to transform error object shapes into a standard structure
        // This is tentative ,might change based on specific requirement that needs to show error in UI
        switch (response.status) {
            case 401:
                response.errorCode = "UN_AUTHORIZED";
                break;
            case 403:
                response.errorCode = "FORBIDDEN";
                break;
            case 404:
                response.errorCode = "NOT_FOUND";
                break;
            case 500:
                response.errorCode = "INTERNAL_SERVER_ERROR";
                break;
            case 200:
                response.successCode = "SUCCESS";
                break;
            default:
                response.errorCode = "";
        }
        return response;
    };

    // request handler that gets fired from interceptor
    const requestHandler = async (request: any) => {
        const fullUrl = request.baseURL + request.url;
        if (localStorage.getItem('shopId') && fullUrl.includes("/shipping/")) {
            request.headers[SHOP_ID_HEADER] = localStorage.getItem('shopId');
        }
        return request;
    };

    const errorHandler = async (error: any) => {
        const status = error?.response?.status;
        const errorCode = error?.response?.data?.errorCode || error?.response?.data?.error;

        if (status === 401) {
            returnToaster(errorCode);
        }
        return await Promise.reject(error);
    };

    customAxios.interceptors.request.use((request: AxiosRequestConfig) =>
        requestHandler(request)
    );

    customAxios.interceptors.response.use(
        (response) => responseHandler(response),
        (error) => errorHandler(error)
    );

    return customAxios;
}
